"use client";

import { useGalleryModal } from "@/features/gallery/useGalleryModal";
import { useIsMobile } from "@/features/layout/useIsMobile";
import type { MediaInfo } from "@/features/media/mediaInfo";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useProjectText } from "@/features/project/useProjectText";
import { useVisit360Modal } from "@/features/visit360/useVisit360Modal";
import { cn } from "@/lib/cn";
import {
  GALLERY_ICONS,
  galleryCardImages,
  type GalleryGroup,
} from "../../poiGalleries";

/**
 * One media type as a card: a thumbnail with the type's icon over it, both
 * overridable per type (see `galleryCardImages`).
 */
export function POIGalleryButton({ group }: { group: GalleryGroup }) {
  const t = useProjectText();
  const configs = useProjectConfigs();
  const media = useMediaContext();
  const isMobile = useIsMobile();
  const gallery = useGalleryModal();
  const visit360 = useVisit360Modal();

  const first: MediaInfo | undefined = group.items[0];
  const { thumbnail, icon } = galleryCardImages(group, configs, media);

  const onOpen = () => {
    // A 360° tour takes the whole viewport rather than a slide in the carousel.
    if (group.type === "street_view" && first?.embedUrl) {
      visit360.open(first.embedUrl);
      return;
    }
    gallery.open(group.items, group.type);
  };

  return (
    <button
      type="button"
      onClick={onOpen}
      className="gallery-card relative cursor-pointer text-left"
    >
      {thumbnail && (
        // Tenant-uploaded, arbitrary dimensions, same-origin.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbnail}
          alt=""
          className={cn("w-full object-cover", isMobile ? "h-28" : "h-36")}
          loading="lazy"
        />
      )}

      <div className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center rounded-full bg-black bg-opacity-50 p-2 text-white">
          {icon ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={icon} alt="" />
          ) : group.type === "street_view" ? (
            <StreetViewIcon />
          ) : (
            <i className={`icon ic-${GALLERY_ICONS[group.type]} text-primary`} />
          )}
        </span>
      </div>

      <div className="flex items-center py-1.5">
        <span className="text-sm">
          {t(`gallery.${group.type}`, group.type)}
        </span>{" "}
        <i className="icon ic-linearicons_arrow-right ml-2" />
      </div>
    </button>
  );
}

/** Street view has no glyph in the icon font, so it ships as markup. */
function StreetViewIcon() {
  return (
    <span className="mr-2 mt-[-4px] inline-block h-5">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="20px"
        height="20px"
        fill="currentColor"
        aria-hidden
      >
        <path d="M0 0h24v24H0z" fill="none" />
        <path d="M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3c1.3 1.5 3.3 2.5 5.5 2.5v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1l-5.2 2.2v4.7h2v-3.4l1.8-.7-1.6 8.1-4.9-1-.4 2 7 1.4z" />
      </svg>
    </span>
  );
}
